import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Mail, Lock, User, ArrowRight, KeyRound } from "lucide-react";

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // login | register | verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptAI, setAcceptAI] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!acceptTerms || !acceptPrivacy) {
      toast.error("Debes aceptar los Términos y la Política de Privacidad");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/register", {
        email, password, name,
        accept_terms: acceptTerms,
        accept_privacy: acceptPrivacy,
        accept_ai: acceptAI,
      });
      toast.success(res.data.email_sent ? "Cuenta creada. Te enviamos un código a tu email." : "Cuenta creada, pero no pudimos enviar el email. Usa 'Reenviar código'.");
      setMode("verify");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data.token) {
        login(res.data.token, res.data.user);
        toast.success("Bienvenido!");
        navigate("/app");
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.needs_verification) {
        toast.info("Verifica tu email primero. Te enviamos un nuevo código.");
        setMode("verify");
      } else {
        toast.error(err.response?.data?.detail || "Credenciales incorrectas");
      }
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/verify", { email, code });
      if (res.data.token) {
        login(res.data.token, res.data.user);
        toast.success("Email verificado!");
        navigate("/app");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código incorrecto");
    } finally { setLoading(false); }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      const res = await api.post("/auth/resend-code", { email });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reenviar el código");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center p-1">
              <img src="/logo.png" alt="CotflowAI" className="w-full h-full object-contain" />
            </div>
            <span className="text-white font-bold text-2xl font-['Satoshi']">CotflowAI</span>
          </div>
          <h1 className="text-4xl font-black text-white font-['Satoshi'] tracking-tight mb-4">Cotizaciones profesionales en minutos</h1>
          <p className="text-slate-400 text-lg leading-relaxed">Crea, personaliza y envía cotizaciones con inteligencia artificial. Tu empresa, tus reglas.</p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent" />
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <FileText size={22} className="text-white" />
            </div>
            <span className="font-bold text-2xl font-['Satoshi']">CotflowAI</span>
          </div>

          {mode === "login" && (
            <form onSubmit={handleLogin} data-testid="login-form">
              <h2 className="text-3xl font-bold font-['Satoshi'] tracking-tight mb-2">Iniciar sesión</h2>
              <p className="text-slate-500 mb-8">Ingresa a tu cuenta para continuar</p>

              <div className="space-y-4">
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    data-testid="login-email" />
                </div>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="password" placeholder="Contraseña" value={password} onChange={e => setPassword(e.target.value)} required
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    data-testid="login-password" />
                </div>
              </div>

              <button type="submit" disabled={loading}
                className="w-full mt-6 bg-slate-900 text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="login-submit">
                {loading ? "Ingresando..." : <><span>Ingresar</span><ArrowRight size={16} /></>}
              </button>

              <p className="text-center text-sm text-slate-500 mt-6">
                ¿No tienes cuenta?{" "}
                <button type="button" onClick={() => setMode("register")} className="text-blue-600 font-semibold hover:underline" data-testid="go-register">Crear cuenta</button>
              </p>
            </form>
          )}

          {mode === "register" && (
            <form onSubmit={handleRegister} data-testid="register-form">
              <h2 className="text-3xl font-bold font-['Satoshi'] tracking-tight mb-2">Crear cuenta</h2>
              <p className="text-slate-500 mb-8">Regístrate para comenzar</p>

              <div className="space-y-4">
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} required
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    data-testid="register-name" />
                </div>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    data-testid="register-email" />
                </div>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="password" placeholder="Contraseña" value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    data-testid="register-password" />
                </div>
              </div>

              <div className="mt-5 space-y-2.5">
                <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-600 leading-relaxed">
                  <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300" data-testid="accept-terms" />
                  <span>
                    Acepto los <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-blue-600 underline">Términos y Condiciones</a>. <span className="text-red-500">*</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-600 leading-relaxed">
                  <input type="checkbox" checked={acceptPrivacy} onChange={e => setAcceptPrivacy(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300" data-testid="accept-privacy" />
                  <span>
                    Acepto la <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-blue-600 underline">Política de Privacidad</a> y el tratamiento de mis datos según se describe en ella. <span className="text-red-500">*</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-600 leading-relaxed">
                  <input type="checkbox" checked={acceptAI} onChange={e => setAcceptAI(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300" data-testid="accept-ai" />
                  <span>
                    Consiento el uso de <strong>funciones de IA</strong> para procesar textos que ingrese en la aplicación (opcional pero recomendado).
                  </span>
                </label>
              </div>

              <button type="submit" disabled={loading || !acceptTerms || !acceptPrivacy}
                className="w-full mt-6 bg-slate-900 text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="register-submit">
                {loading ? "Creando..." : <><span>Crear cuenta</span><ArrowRight size={16} /></>}
              </button>

              <p className="text-center text-sm text-slate-500 mt-6">
                ¿Ya tienes cuenta?{" "}
                <button type="button" onClick={() => setMode("login")} className="text-blue-600 font-semibold hover:underline" data-testid="go-login">Iniciar sesión</button>
              </p>
            </form>
          )}

          {mode === "verify" && (
            <form onSubmit={handleVerify} data-testid="verify-form">
              <h2 className="text-3xl font-bold font-['Satoshi'] tracking-tight mb-2">Verificar email</h2>
              <p className="text-slate-500 mb-6">Te enviamos un código de 6 dígitos a <strong>{email}</strong>. Revisa tu bandeja de entrada (y spam).</p>

              <div className="relative">
                <KeyRound size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" placeholder="Código de 6 dígitos" value={code} onChange={e => setCode(e.target.value)} required maxLength={6}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 bg-white text-sm text-center text-2xl tracking-[0.3em] font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  data-testid="verify-code" />
              </div>

              <button type="submit" disabled={loading}
                className="w-full mt-6 bg-slate-900 text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-all active:scale-[0.98] disabled:opacity-50"
                data-testid="verify-submit">
                {loading ? "Verificando..." : "Verificar"}
              </button>

              <p className="text-center text-sm text-slate-500 mt-6">
                ¿No te llegó el código?{" "}
                <button type="button" onClick={handleResendCode} disabled={resending} className="text-blue-600 font-semibold hover:underline disabled:opacity-50" data-testid="resend-code-btn">
                  {resending ? "Enviando..." : "Reenviar código"}
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
